import { OverlayModule } from '@angular/cdk/overlay';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ErrorHandler,
  isDevMode,
  NgModule,
} from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { ButtonModule, IconModule } from '@zipari/design-system';
import { TimeoutModalModule } from '@zipari/shared-sbp-modules';
import { PipeModule } from '@zipari/shared-sbp-pipes';
import { ConfirmationModalComponent } from '@zipari/shared-sbp-components';
import { ConfirmationModalWrapperComponent } from '@zipari/shared-sbp-services';
import {
  AnalyticsService,
  AuthService,
  ConfigService,
  MtmService,
  SentryErrorHandlerService,
  STATE_COHORT_PROVIDER,
  SBPServicesModule,
  LoggingInterceptor,
} from '@zipari/shared-sbp-services';
import {
  AddressPipe,
  BuildQueryPipe,
  DoesListHaveValuesPipe,
  EqualityFilterPipe,
  FullNamePipe,
  GetValuePipe,
  MaskPipe,
  PhoneNumberPipe,
  PipesModule,
  ReadMorePipe,
  SafeNumberPipe,
  StringBuilderPipe,
} from '@zipari/web-utils';
import { CONFIG } from '../config';
import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { CoreModule } from './shared/core.module';
import { SitemapComponent } from './sitemap.component';
import {} from '../../../../libs/app-base-v3/shared/services/src/lib/logging.interceptor';

@NgModule({
  declarations: [
    AppComponent,
    ConfirmationModalComponent,
    ConfirmationModalWrapperComponent,
    SitemapComponent,
  ],
  imports: [
    AppRoutingModule,
    RouterModule,
    BrowserModule,
    BrowserAnimationsModule,
    OverlayModule,
    PipeModule,
    CoreModule.forRoot(),
    PipesModule,
    NgIdleKeepaliveModule.forRoot(),
    SBPServicesModule.forRoot(environment),
    TimeoutModalModule,
    ButtonModule,
    IconModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: resolveConfigUserData,
      deps: [ConfigService, AuthService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoggingInterceptor,
      multi: true,
    },
    // {
    //     provide: ErrorHandler,
    //     useClass: SentryErrorHandlerService,
    // },
    Title,
    AnalyticsService,
    MtmService,
    StringBuilderPipe,
    AddressPipe,
    GetValuePipe,
    EqualityFilterPipe,
    BuildQueryPipe,
    DoesListHaveValuesPipe,
    FullNamePipe,
    PhoneNumberPipe,
    MaskPipe,
    ReadMorePipe,
    SafeNumberPipe,
    CurrencyPipe,
    DatePipe,
    STATE_COHORT_PROVIDER,
    CurrencyPipe,
    DatePipe,
  ],
  exports: [],
  bootstrap: [AppComponent],
})
export class AppModule {}

export function resolveConfigUserData(
  configService: ConfigService,
  authService: AuthService,
) {
  let configPromise;
  let useLocalConfig = false;

  if (isDevMode() && !window['CONFIG']) {
    window['CONFIG'] = CONFIG;
  }

  if (typeof script_useLocalConfig !== 'undefined') {
    // gets called if local development using command line configuration for useLocalConfig
    useLocalConfig = script_useLocalConfig;
  }

  if (useLocalConfig) {
    configPromise = configService.initializeLocalConfig(environment['app']);
  } else if (!useLocalConfig && overrideDbFromLocalConfig) {
    configPromise = configService.initializeDBConfigWithLocalOverride(
      environment['app'],
      {
        setConfig: true,
        returnFullConfig: true,
      },
    );
  } else {
    configPromise = configService.initializeConfig();
  }

  return () => {
    // Ensures logged in user set only after config and user data fetched
    return configPromise.then((config) => {
      let user = {};

      authService.setAppConfig(config.APP);

      try {
        const configUser = config.USER || '{}';
        user = JSON.parse(configUser);
      } catch (err) {
        if (isDevMode()) {
          console.error(err);
        }
      }

      if (!environment['configs']?.['ignoreAuth']) {
        authService.setLoggedInUser(user, environment['configs']);
      }
    });
  };
}
